import ProjectDetailsV2Enhanced from "shared-ui/src/features/projects-feature/pages/ProjectDetailsV2Enhanced";
import { Site } from "shared-ui/src/features/projects-feature/canonical";
import { ContractsProvider } from "shared-ui/src/features/contracts-feature/context/ContractsContext";
import { Site as ContractSite } from "shared-ui/src/features/contracts-feature/canonical";
import useGetContractById from "@features/projects-feature/hooks/useGetContractById";
import { ContractDetailsDialog } from "@features/projects-feature/components/ContractDetailsDialog";
import useGetProjectById from "@features/projects-feature/hooks/useGetProjectById";
import useGetProjectDetail from "@features/projects-feature/hooks/useGetProjectDetail";
import useGetResourcesByIds from "./hooks/useGetResourcesByIds";
import { IRootState } from "../../../../../modules";
import { useAppSelector } from "../../../../../functions/hooks/hooks";
import {
  IncludeExcludeFilters as NSwagIncludeExcludeFilters,
  ProjectDetailRequest,
} from "@lib/ShiOneClient";
import type { ProjectDetailParams } from "shared-ui/src/features/projects-feature/canonical";
import { useLocation } from "react-router-dom";
import { AppendIfPrefixExists } from "../../../../../modules/sitePaths";
import useGetLinkedItProjects from "@features/projects-feature/hooks/useGetLinkedItProjects";
import {
  useGetAccounts,
  useGetContractData,
} from "@features/services-feature/contracts/hooks";
import useGetDetailedRetainerExpenseByContractId from "@features/services-feature/useGetDetailedRetainerExpenseByContractId";
import useGetRecurringServicesContractDetailByContractId from "@features/services-feature/useGetRecurringServicesContractDetailByContractId";
import { useContractRecordsDetailFieldsForContracts } from "@features/projects-feature/utils/fields/ContractDetailsFields";
import { useCXUserPrefs } from "@basecomponents/mui-data-grid/useCXUserPrefs";
import { usageBalanceHookRegistry } from "@features/services-feature/contracts/usageBalanceHookRegistry";
import { useClaims } from "../../../../../auth/ClaimsProvider";

/**
 * Wrapper hook that adapts the canonical ProjectDetailParams interface
 * to NSwag's ProjectDetailRequest class.
 *
 * This is necessary because NSwag generates classes while the canonical
 * interface uses plain objects.
 */
const useGetProjectDetailWrapper = (params: ProjectDetailParams) => {
  const nswagRequest = new ProjectDetailRequest({
    projectId: params.projectId,
    includeFilters: params.includeFilters
      ? new NSwagIncludeExcludeFilters(params.includeFilters)
      : undefined,
    excludeFilters: params.excludeFilters
      ? new NSwagIncludeExcludeFilters(params.excludeFilters)
      : undefined,
    groupBy: params.groupBy ?? undefined,
    sortBy: params.sortBy ?? undefined,
  });
  return useGetProjectDetail(nswagRequest);
};

// Wrapper hook to adapt Redux picklist to the expected { data } interface
const usePicklistWrapper = () => {
  const { picklist } = useAppSelector((s: IRootState) => s.picklist);
  return { data: picklist };
};

function ProjectDetailsV2() {
  const { search, state } = useLocation();
  const { isShi } = useClaims();
  const listUrl = (state as { listUrl?: string } | null)?.listUrl;

  return (
    <ProjectDetailsV2Enhanced
      useGetProjectById={useGetProjectById}
      useGetProjectDetail={useGetProjectDetailWrapper}
      useGetContractById={useGetContractById}
      usePicklist={usePicklistWrapper}
      useGetResourcesByIds={useGetResourcesByIds}
      useLinkedItProjects={useGetLinkedItProjects}
      buildItProjectHref={(project) =>
        AppendIfPrefixExists(`/projects/${project.itProjectId}/orders`)
      }
      renderContractDialog={({ open, onClose, contract, picklist }) => (
        <ContractsProvider
          useGetAccounts={useGetAccounts}
          useGetContractData={useGetContractData}
          useGetDetailedRetainerExpenseByContractId={
            useGetDetailedRetainerExpenseByContractId
          }
          useGetRecurringServicesContractDetailByContractId={
            useGetRecurringServicesContractDetailByContractId
          }
          useContractRecordsDetailFields={
            useContractRecordsDetailFieldsForContracts
          }
          userPrefHook={useCXUserPrefs}
          siteContext={ContractSite.CustomerExperience}
          usageBalanceHookRegistry={usageBalanceHookRegistry}
          isValidShi={isShi()}
        >
          <ContractDetailsDialog
            open={open}
            onClose={onClose}
            contract={contract as never}
            picklist={picklist}
          />
        </ContractsProvider>
      )}
      backButtonUrl={
        listUrl ??
        AppendIfPrefixExists(`/services/service-engagements${search}`)
      }
      backButtonSize="small"
      site={Site.CustomerExperience}
    />
  );
}

export default ProjectDetailsV2;
